// The Fund page's positions (projects/investing spec §5.5, walkthrough 11):
// the account as the latest snapshot read it, beside the approved target.
//
// The API gives each position's value and the account's total, not a
// weight; a weight here is value ÷ total value, worked out in whole
// hundredths of a percent with BigInt, half up like the API — never through
// floats (2.01 ÷ 200.00 is 1.005%: 1.01%, where a float's toFixed says
// 1.00). Target weights are shown as the API gives them.

import { divideRounded, formatUsd, scaled } from "./money";

const MINUS = "−";

/** value ÷ total as hundredths of a percent (BigInt), or null when there's no total to divide by. */
function weightOf(value, totalCents) {
  const cents = scaled(value, 2);
  if (cents === null || totalCents === null || totalCents <= 0n) return null;
  return divideRounded(cents * 10000n, totalCents);
}

const difference = (now, target) => (now === null || target === null ? null : now - scaled(target, 2));

/**
 * The snapshot's positions joined to the approved target (`current`, or
 * null before one is approved), by symbol:
 * `{rows: [{symbol, name, quantity, price, value, now, target, drift, state}], cash: {value, now, target, drift}}`.
 * `now` and `drift` are hundredths of a percent (BigInt); `target` is the
 * API's weight string. `state`: held (in both), not_held (a target holding
 * the account doesn't hold yet — 0% now), off_target (held, but not in the
 * target or at 0% there: a sync sells it), no_target (nothing approved).
 * A 0% target holding the account doesn't hold is left out — its sale is
 * done.
 */
export function positionRows(snapshot, current) {
  const totalCents = scaled(snapshot?.total_value, 2);
  const targets = new Map();
  for (const holding of current?.holdings ?? []) {
    if ((scaled(holding.weight, 2) ?? 0n) > 0n) targets.set(holding.symbol, holding);
  }
  const rows = [];
  const held = new Set();
  for (const position of snapshot?.positions ?? []) {
    held.add(position.symbol);
    const holding = targets.get(position.symbol) ?? null;
    const now = weightOf(position.value, totalCents);
    let state = "no_target";
    if (current) state = holding ? "held" : "off_target";
    const target = holding ? holding.weight : null;
    rows.push({
      symbol: position.symbol,
      name: holding?.name ?? null,
      quantity: position.quantity,
      price: position.price,
      value: position.value,
      now,
      target,
      drift: current ? difference(now, target ?? "0") : null,
      state,
    });
  }
  for (const holding of targets.values()) {
    if (held.has(holding.symbol)) continue;
    const now = totalCents !== null && totalCents > 0n ? 0n : null;
    rows.push({
      symbol: holding.symbol,
      name: holding.name ?? null,
      quantity: null,
      price: null,
      value: null,
      now,
      target: holding.weight,
      drift: difference(now, holding.weight),
      state: "not_held",
    });
  }
  rows.sort((a, b) => (a.symbol < b.symbol ? -1 : a.symbol > b.symbol ? 1 : 0));
  const cashNow = weightOf(snapshot?.cash, totalCents);
  const cashTarget = current ? current.cash_weight : null;
  return {
    rows,
    cash: { value: snapshot?.cash ?? null, now: cashNow, target: cashTarget, drift: difference(cashNow, cashTarget) },
  };
}

function hundredths(h) {
  const digits = (h < 0n ? -h : h).toString().padStart(3, "0");
  return `${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

/** Hundredths of a percent (BigInt) as "13.81%"; null for none. */
export function formatHundredths(h) {
  if (typeof h !== "bigint") return null;
  return `${h < 0n ? MINUS : ""}${hundredths(h)}%`;
}

/** A difference in hundredths of a percent as signed points: "+0.81 pp", "−4.00 pp", "0.00 pp". */
export function formatHundredthsPoints(h) {
  if (typeof h !== "bigint") return null;
  if (h === 0n) return "0.00 pp";
  return `${h < 0n ? MINUS : "+"}${hundredths(h)} pp`;
}

const DECIMAL = /^(-?)(\d+)(?:\.(\d+))?$/;
const isZero = (raw) => /^-?0*(\.0*)?$/.test(raw);

/** A simple return as the API gives it (a % string), signed: "+4.12%", "−1.20%", "0.00%"; null for none. */
export function formatReturnPct(raw) {
  const match = typeof raw === "string" ? DECIMAL.exec(raw) : null;
  if (!match) return null;
  const digits = raw.replace(/^-/, "");
  if (isZero(raw)) return `${digits}%`;
  return `${match[1] ? MINUS : "+"}${digits}%`;
}

/**
 * A tax rate (the API's fraction, "0.3200") as a percentage, exactly:
 * "32%", "32.5%" — the decimal point moved two places, trailing zeros off.
 */
export function formatRatePct(raw) {
  const match = typeof raw === "string" ? DECIMAL.exec(raw) : null;
  if (!match || match[1]) return null;
  const fraction = (match[3] ?? "").padEnd(2, "0");
  const whole = `${match[2]}${fraction.slice(0, 2)}`.replace(/^0+(?=\d)/, "");
  const rest = fraction.slice(2).replace(/0+$/, "");
  return `${whole}${rest ? `.${rest}` : ""}%`;
}

/** A difference in dollars, signed: "+$345.45", "−$555.33", "$0.00". */
export function formatSignedUsd(raw) {
  const formatted = formatUsd(raw);
  if (formatted === null) return null;
  if (isZero(raw) || formatted.startsWith(MINUS)) return formatted;
  return `+${formatted}`;
}
